import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

class CartCrossSell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!target || !this.contains(target)) return;

    switch (target.getAttribute('data-action')) {
      case 'prev':
        return this.#scroll(-1);
      case 'next':
        return this.#scroll(1);
      case 'add':
        return this.#add(target);
    }
  };

  #onChange = (event) => {
    const colorSelect = event.target instanceof Element ? event.target.closest('.cart-cross-sell__color') : null;
    if (colorSelect instanceof HTMLSelectElement) {
      this.#selectColor(colorSelect);
      return;
    }

    const select = event.target instanceof Element ? event.target.closest('.cart-cross-sell__select') : null;
    if (!(select instanceof HTMLSelectElement)) return;

    const panel = select.closest('.cart-cross-sell__panel');
    const option = select.selectedOptions[0];
    if (!panel || !option) return;

    const current = panel.querySelector('.cart-cross-sell__price-current');
    const compare = panel.querySelector('.cart-cross-sell__price-compare');
    if (current) current.textContent = option.dataset.price || '';
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }

    const image = panel.querySelector('.cart-cross-sell__image img');
    if (image instanceof HTMLImageElement && option.dataset.image) {
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
    }

    const button = panel.querySelector('.cart-cross-sell__add');
    if (button) {
      const available = option.dataset.available === 'true';
      button.disabled = !available;
      button.textContent = available ? button.dataset.label || 'Add' : button.dataset.soldOutLabel || 'Sold out';
    }

    this.#clearError(panel);
  };

  #scroll(direction) {
    const track = this.querySelector('.cart-cross-sell__track');
    if (!(track instanceof HTMLElement)) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    const atStart = track.scrollLeft <= 4;

    if (direction > 0 && atEnd) {
      track.scrollTo({ left: 0, behavior });
    } else if (direction < 0 && atStart) {
      track.scrollTo({ left: track.scrollWidth, behavior });
    } else {
      track.scrollBy({ left: direction * track.clientWidth, behavior });
    }
  }

  #selectColor(select) {
    const card = select.closest('.cart-cross-sell__card');
    const targetId = select.value;
    if (!card || !targetId) return;

    card.querySelectorAll('.cart-cross-sell__panel').forEach((panel) => {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.productId !== targetId;
    });
  }

  #clearError(panel) {
    const error = panel.querySelector('.cart-cross-sell__error');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  async #add(button) {
    const panel = button.closest('.cart-cross-sell__panel');
    if (!panel || button.disabled) return;

    const select = panel.querySelector('.cart-cross-sell__select');
    const variantId = select instanceof HTMLSelectElement ? select.value : panel.dataset.variantId;
    if (!variantId) return;

    const cartItems = document.querySelector('cart-items-component');
    const sectionId = cartItems?.dataset.sectionId || 'cart-drawer-section';
    const originalLabel = button.textContent;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#clearError(panel);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const fetchCart = async () => {
      if (cartItems && typeof cartItems.fetchCartData === 'function') {
        return cartItems.fetchCartData();
      }
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      return response.json();
    };

    try {
      const body = JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then((r) => r.json());
      const ajaxCart = await fetchCart();

      if (response.status) {
        const message = response.description || response.message || 'Could not add this item.';

        this.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description, errors: response.errors },
          })
        );

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: true, items: ajaxCart.items, source: 'cart-cross-sell' },
        });

        const error = panel.querySelector('.cart-cross-sell__error');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-cross-sell',
          sourceId: this.id || 'cart-cross-sell',
          itemCount: 1,
          sections: response.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}